import type { Lang } from './types';

// The countries the app knows, keyed by the Spanish name the server stores.
// Each one has coordinates on the server, so delivery times can be worked out.
const COUNTRIES: Array<{ key: string; code: string; en: string }> = [
  { key: 'ecuador', code: 'EC', en: 'Ecuador' },
  { key: 'colombia', code: 'CO', en: 'Colombia' },
  { key: 'perú', code: 'PE', en: 'Peru' },
  { key: 'méxico', code: 'MX', en: 'Mexico' },
  { key: 'argentina', code: 'AR', en: 'Argentina' },
  { key: 'chile', code: 'CL', en: 'Chile' },
  { key: 'brasil', code: 'BR', en: 'Brazil' },
  { key: 'estados unidos', code: 'US', en: 'United States' },
  { key: 'canadá', code: 'CA', en: 'Canada' },
  { key: 'españa', code: 'ES', en: 'Spain' },
  { key: 'portugal', code: 'PT', en: 'Portugal' },
  { key: 'francia', code: 'FR', en: 'France' },
  { key: 'reino unido', code: 'GB', en: 'United Kingdom' },
  { key: 'países bajos', code: 'NL', en: 'Netherlands' },
  { key: 'alemania', code: 'DE', en: 'Germany' },
  { key: 'italia', code: 'IT', en: 'Italy' },
];

export function isKnownCountry(key: string): boolean {
  return COUNTRIES.some((c) => c.key === key);
}

/** The flag emoji, built from the country's two regional-indicator letters. */
export function flagOf(key: string): string {
  const code = COUNTRIES.find((c) => c.key === key)?.code;
  if (!code) return '';
  return String.fromCodePoint(...[...code].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

export function countryName(key: string, lang: Lang): string {
  const c = COUNTRIES.find((x) => x.key === key);
  if (!c) return key;
  return lang === 'en' ? c.en : c.key.replace(/(^|\s)\p{L}/gu, (m) => m.toUpperCase());
}

/** "🇬🇧 Reino Unido". */
export function countryLabel(key: string, lang: Lang): string {
  const flag = flagOf(key);
  return flag ? `${flag} ${countryName(key, lang)}` : countryName(key, lang);
}

/** Every country for a picker, alphabetical in the reader's language. */
export function countryOptions(lang: Lang): Array<{ key: string; label: string }> {
  return COUNTRIES.map((c) => ({ key: c.key, name: countryName(c.key, lang), label: countryLabel(c.key, lang) }))
    .sort((a, b) => a.name.localeCompare(b.name, lang))
    .map(({ key, label }) => ({ key, label }));
}
